import { api } from '../api/client';
import { encryptAndUploadBlob, type BlobUploadProgress } from '../api/blobUpload';
import {
  decryptFileFromChunks,
  splitPackedChunks,
  type ChunkedFileManifest,
} from './chunkedFile';
import {
  decodeBase64,
  decryptAesGcm,
  encodeBase64,
  encryptAesGcm,
  hkdfSha256,
  importAesKey,
  packEncryptedBlob,
  unpackEncryptedBlob,
} from './primitives';
import {
  appendManifestEntries,
  commitManifestMutation,
  replaceFileContentsInManifest,
  type SharedFolderActorRef,
  type SharedFolderFileEntry,
  type SharedFolderManifestPlaintext,
} from './sharedFolderManifest';

const FILE_CONTENT_KEY_INFO = 'shareweb:folder-file-key:v1';
const FILE_CONTENT_KEY_AAD = 'shareweb:folder-file-key:v1';

async function wrapFileContentKey(
  folderKey: Uint8Array,
  fileId: string,
  contentKey: Uint8Array,
): Promise<string> {
  const material = await hkdfSha256(
    folderKey,
    new TextEncoder().encode(fileId),
    FILE_CONTENT_KEY_INFO,
    32,
  );
  const aesKey = await importAesKey(material);
  const encrypted = await encryptAesGcm(
    aesKey,
    contentKey,
    new TextEncoder().encode(FILE_CONTENT_KEY_AAD),
  );
  return encodeBase64(packEncryptedBlob(encrypted));
}

async function unwrapFileContentKey(
  folderKey: Uint8Array,
  fileId: string,
  wrappedContentKey: string,
): Promise<Uint8Array> {
  const material = await hkdfSha256(
    folderKey,
    new TextEncoder().encode(fileId),
    FILE_CONTENT_KEY_INFO,
    32,
  );
  const aesKey = await importAesKey(material);
  return decryptAesGcm(
    aesKey,
    unpackEncryptedBlob(decodeBase64(wrappedContentKey)),
    new TextEncoder().encode(FILE_CONTENT_KEY_AAD),
  );
}

function buildChunkedManifestFromEntry(entry: SharedFolderFileEntry, contentKey: Uint8Array): ChunkedFileManifest {
  const ciphertextSizes = entry.chunkCiphertextSizes
    ?? Array.from({ length: entry.chunkCount }, () => 0);

  return {
    fileId: entry.fileId,
    contentKey,
    plaintextSha256: entry.contentHash,
    totalPlaintextBytes: entry.size,
    chunkPlaintextBytes: entry.chunkPlaintextBytes,
    chunks: Array.from({ length: entry.chunkCount }, (_, chunkIndex) => ({
      chunkIndex,
      ciphertextSizeBytes: ciphertextSizes[chunkIndex] ?? 0,
    })),
  };
}

async function downloadEncryptedBlob(
  fileId: string,
  options: { folderId: string } | { folderToken: string; guestInviteId: string },
): Promise<Uint8Array> {
  const fetchChunk = 'folderId' in options
    ? (index: number) => api.fetchSharedFolderFileChunk(options.folderId, fileId, index)
    : (index: number) => api.fetchSharedFolderGuestFileChunk(
        options.folderToken,
        options.guestInviteId,
        fileId,
        index,
      );

  const first = await fetchChunk(0);
  if (first.totalSizeBytes === 0 && first.totalChunks <= 0) {
    return new Uint8Array();
  }

  if (first.totalChunks <= 1) {
    return first.bytes;
  }

  const parts = [first.bytes];
  for (let index = 1; index < first.totalChunks; index++) {
    parts.push((await fetchChunk(index)).bytes);
  }

  const output = new Uint8Array(parts.reduce((sum, part) => sum + part.length, 0));
  let offset = 0;
  for (const part of parts) {
    output.set(part, offset);
    offset += part.length;
  }

  return output;
}

export { formatBytes as formatFileSize } from '../i18n/formatBytes';

export async function uploadFileToSharedFolder(input: {
  folderId: string;
  folderKey: Uint8Array;
  revision: number;
  manifest: SharedFolderManifestPlaintext;
  currentPath: string;
  file: File;
  actor?: SharedFolderActorRef;
  onProgress?: (progress: BlobUploadProgress) => void;
}): Promise<{
  revision: number;
  manifest: SharedFolderManifestPlaintext;
  conflicts: Array<{ originalPath: string; resolvedPath: string }>;
}> {
  const fileName = input.file.name.trim();
  if (!fileName) {
    throw new Error('empty_file_name');
  }

  const path = input.currentPath ? `${input.currentPath}/${fileName}` : fileName;

  const fileId = crypto.randomUUID();
  const { blobId, manifest: chunkManifest } = await encryptAndUploadBlob(input.file, {
    fileId,
    onProgress: input.onProgress,
  });
  const wrappedContentKey = await wrapFileContentKey(input.folderKey, fileId, chunkManifest.contentKey);
  const chunkCiphertextSizes = chunkManifest.chunks.map((chunk) => chunk.ciphertextSizeBytes);
  const fileEntry: SharedFolderFileEntry = {
    type: 'file',
    path,
    fileId,
    blobId,
    contentHash: chunkManifest.plaintextSha256,
    size: chunkManifest.totalPlaintextBytes,
    chunkPlaintextBytes: chunkManifest.chunkPlaintextBytes,
    chunkCount: chunkManifest.chunks.length,
    chunkCiphertextSizes: chunkManifest.chunks.length > 1 ? chunkCiphertextSizes : undefined,
    wrappedContentKey,
    contentType: input.file.type || undefined,
    createdAt: new Date().toISOString(),
    createdBy: input.actor,
    updatedAt: new Date().toISOString(),
    updatedBy: input.actor,
  };

  const committed = await appendManifestEntries(
    input.folderId,
    input.folderKey,
    input.revision,
    input.manifest,
    [fileEntry],
  );

  return committed;
}

export async function replaceSharedFolderFileContents(input: {
  folderId: string;
  folderKey: Uint8Array;
  revision: number;
  manifest: SharedFolderManifestPlaintext;
  fileId: string;
  file: Blob;
  actor?: SharedFolderActorRef;
}): Promise<{
  revision: number;
  manifest: SharedFolderManifestPlaintext;
  entry: SharedFolderFileEntry;
}> {
  const existing = input.manifest.entries.find(
    (entry): entry is SharedFolderFileEntry => entry.type === 'file' && entry.fileId === input.fileId,
  );
  if (!existing) {
    throw new Error('file_deleted');
  }

  const { blobId, manifest: chunkManifest } = await encryptAndUploadBlob(input.file, {
    fileId: input.fileId,
  });
  const wrappedContentKey = await wrapFileContentKey(
    input.folderKey,
    input.fileId,
    chunkManifest.contentKey,
  );
  const chunkCiphertextSizes = chunkManifest.chunks.map((chunk) => chunk.ciphertextSizeBytes);
  const patch = {
    blobId,
    contentHash: chunkManifest.plaintextSha256,
    size: chunkManifest.totalPlaintextBytes,
    chunkPlaintextBytes: chunkManifest.chunkPlaintextBytes,
    chunkCount: chunkManifest.chunks.length,
    chunkCiphertextSizes: chunkManifest.chunks.length > 1 ? chunkCiphertextSizes : undefined,
    wrappedContentKey,
    contentType: input.file.type || existing.contentType,
    updatedAt: new Date().toISOString(),
    updatedBy: input.actor,
  };

  const committed = await commitManifestMutation(
    input.folderId,
    input.folderKey,
    input.revision,
    input.manifest,
    (latest) => replaceFileContentsInManifest(latest, input.fileId, patch),
  );
  const entry = committed.manifest.entries.find(
    (item): item is SharedFolderFileEntry => item.type === 'file' && item.fileId === input.fileId,
  );
  if (!entry) {
    throw new Error('file_deleted');
  }

  return { revision: committed.revision, manifest: committed.manifest, entry };
}

export async function downloadSharedFolderFile(input: {
  folderKey: Uint8Array;
  entry: SharedFolderFileEntry;
  folderId?: string;
  guestRoute?: { folderToken: string; guestInviteId: string };
}): Promise<Uint8Array> {
  if (!input.folderId && !input.guestRoute) {
    throw new Error('download_route_required');
  }

  const contentKey = await unwrapFileContentKey(
    input.folderKey,
    input.entry.fileId,
    input.entry.wrappedContentKey,
  );
  const encryptedBlob = await downloadEncryptedBlob(
    input.entry.fileId,
    input.folderId
      ? { folderId: input.folderId }
      : input.guestRoute!,
  );
  const chunkManifest = buildChunkedManifestFromEntry(input.entry, contentKey);
  if (input.entry.chunkCount === 1 && (chunkManifest.chunks[0]?.ciphertextSizeBytes ?? 0) === 0) {
    chunkManifest.chunks = [{ chunkIndex: 0, ciphertextSizeBytes: encryptedBlob.length }];
  }

  const encryptedChunks = splitPackedChunks(
    encryptedBlob,
    chunkManifest.chunks.map((chunk) => chunk.ciphertextSizeBytes),
  );
  return decryptFileFromChunks(chunkManifest, encryptedChunks);
}

export function triggerBrowserDownload(fileName: string, bytes: Uint8Array): void {
  const copy = new Uint8Array(bytes);
  const blob = new Blob([copy], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}
